import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { notifications } from '../api/notifications';
import { useSocket, useSocketEvent } from './SocketContext.jsx';
import MoksyLogger from '../utils/logger';
import { storage } from '../utils/storage';

const NotificationsContext = createContext({
  items: [],
  unreadCount: 0,
  loading: true,
  refresh: () => {},
  markRead: () => {}
});

export const NotificationsProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const initializedRef = useRef(false);
  const authRef = useRef(storage.hasUsableAccessToken());
  const { connected } = useSocket();

  const refresh = async () => {
    // NOTE: this used to hard-return empty whenever `import.meta.env.DEV` was
    // set, which silently disabled the in-app bell for the ENTIRE local stack
    // — the exact environment the product is demoed and tested in. The backend
    // was storing unread notifications correctly (CHAT_MESSAGE, review events,
    // …); the frontend just refused to fetch them, so "the bell never
    // populates, only email does" looked like a delivery bug when it was a
    // one-line dev stub. The 401 / 503 handling below already degrades
    // gracefully when the service is unreachable, so there is nothing dev-only
    // to guard against here.
    if (!storage.hasUsableAccessToken()) {
      setItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await notifications.list();
      setItems(data?.items || []);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 503) {
        setItems([]);
      } else if (status !== 401) {
        MoksyLogger.error('NOTIFICATIONS.SYNC_FAILED', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (id) => {
    try {
      await notifications.markRead(id);
      await refresh();
    } catch (error) {
      MoksyLogger.error('NOTIFICATIONS.MARK_READ_FAILED', error);
    }
  };

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    refresh();
  }, []);

  // The provider mounts ONCE, outside AuthContext, so a single on-mount fetch
  // is not enough. When the app boots logged-out (at /login) that fetch finds
  // no session and returns empty; an in-page login never remounts the provider,
  // so without this the bell stayed empty for the whole session until a manual
  // reload. Mirror the socket's token re-check (SocketContext.TOKEN_POLL_MS)
  // and re-sync on the session transition itself: fetch on login / token
  // rotation, clear on logout. Cheap — it reads a local token, not the API.
  useEffect(() => {
    const id = setInterval(() => {
      const authed = storage.hasUsableAccessToken();
      if (authed === authRef.current) return;
      authRef.current = authed;
      if (authed) { refresh(); }
      else { setItems([]); setLoading(false); }
    }, 4000);
    return () => clearInterval(id);
  }, []);

  // Re-sync when the notifications socket (re)connects — a login brings it up
  // faster than the poll above, and a reconnect after a dropped connection
  // pulls in anything that arrived while we were offline (the bell/reconnect
  // gap this used to have).
  useEffect(() => {
    if (connected && storage.hasUsableAccessToken()) refresh();
  }, [connected]);

  // Live updates: whenever the socket delivers a `new_notification`,
  // re-sync the bell so it reflects the new item without a page reload.
  // Defensive + backward compatible — if the socket never connects this
  // simply never fires and the on-mount fetch remains the source of truth.
  useSocketEvent(() => {
    if (!storage.hasUsableAccessToken()) return;
    refresh();
  });

  const unreadCount = useMemo(() => items.filter((item) => !item.isRead).length, [items]);

  const value = useMemo(() => ({
    items,
    unreadCount,
    loading,
    refresh,
    markRead
  }), [items, unreadCount, loading]);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationsContext);
